import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Users, 
  FileText, 
  Image, 
  Settings,
  LogOut,
  Menu,
  X,
  Upload,
  Tag,
  Wrench,
  Phone,
  FolderTree,
  Truck,
  Images,
  Bell,
  Ticket,
  Star,
  RotateCcw,
  BarChart3,
  Scale,
  Mail,
  ClipboardList
} from 'lucide-react';

const AdminLayout = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Admin UI is only supported in 3 languages: SK, RU, EN.
  // If user enters with another language (cs/de), force-switch to a close match
  // and remember original to restore on leaving /admin.
  const ADMIN_LANGS = ['sk', 'ru', 'en'];
  const LANG_FALLBACK = { cs: 'sk', de: 'en' };

  useEffect(() => {
    const current = (i18n.language || 'sk').split('-')[0];
    if (!ADMIN_LANGS.includes(current)) {
      // Save original public language and switch
      if (!sessionStorage.getItem('preAdminLang')) {
        sessionStorage.setItem('preAdminLang', current);
      }
      i18n.changeLanguage(LANG_FALLBACK[current] || 'sk');
    }
    return () => {
      // On leaving admin, restore original public language
      const saved = sessionStorage.getItem('preAdminLang');
      if (saved) {
        i18n.changeLanguage(saved);
        sessionStorage.removeItem('preAdminLang');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchAdminLang = (lng) => {
    // User-initiated change inside admin — clear the "auto-restore" marker
    sessionStorage.removeItem('preAdminLang');
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    if (loading) return;
    
    if (!isAuthenticated) {
      navigate('/login?redirect=/admin');
      return;
    }
    if (user?.role !== 'admin') {
      navigate('/');
    }
  }, [isAuthenticated, user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, labelKey: 'admin.dashboard', exact: true },
    { path: '/admin/orders', icon: ShoppingCart, labelKey: 'admin.orders.menu' },
    { path: '/admin/supply', icon: Truck, labelKey: 'admin.supply', fallback: 'Zásoby a objednávky' },
    { path: '/admin/categories', icon: FolderTree, labelKey: 'admin.categories_menu' },
    { path: '/admin/products', icon: Package, labelKey: 'admin.products' },
    { path: '/admin/import', icon: Upload, labelKey: 'admin.import_export' },
    { path: '/admin/media', icon: Images, labelKey: 'admin.media' },
    { path: '/admin/users', icon: Users, labelKey: 'admin.users' },
    { path: '/admin/blog', icon: FileText, labelKey: 'admin.blog' },
    { path: '/admin/gallery', icon: Image, labelKey: 'admin.gallery' },
    { path: '/admin/brands', icon: Tag, labelKey: 'nav.brands' },
    { path: '/admin/services', icon: Wrench, labelKey: 'nav.services' },
    { path: '/admin/contact', icon: Phone, labelKey: 'nav.contact' },
    { path: '/admin/stock-notifications', icon: Bell, labelKey: 'admin.stock_notifications.menu' },
    { path: '/admin/stock-adjustments', icon: ClipboardList, labelKey: 'admin.stock_history.menu', fallback: 'História korekcií' },
    { path: '/admin/coupons', icon: Ticket, labelKey: 'admin.coupons.menu' },
    { path: '/admin/reviews', icon: Star, labelKey: 'admin.reviews.menu' },
    { path: '/admin/invoices', icon: FileText, labelKey: 'admin.invoices.menu', fallback: 'Faktúry' },
    { path: '/admin/returns', icon: RotateCcw, labelKey: 'admin.returns.menu', fallback: 'Vrátenia tovaru' },
    { path: '/admin/analytics', icon: BarChart3, labelKey: 'admin.analytics.menu', fallback: 'Analytika' },
    { path: '/admin/legal-pages', icon: Scale, labelKey: 'admin.legal.menu', fallback: 'Právne stránky' },
    { path: '/admin/newsletter', icon: Mail, labelKey: 'admin.newsletter.menu', fallback: 'Newsletter' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-gray-900 text-white p-4 flex items-center justify-between">
        <h1 className="text-xl font-bold">Sunify Admin</h1>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-64 bg-gray-900 text-white transform transition-transform duration-200
          flex flex-col h-screen
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 hidden lg:block flex-shrink-0">
            <h1 className="text-2xl font-bold">Sunify Admin</h1>
            {/* Admin language switcher (SK / RU / EN only) */}
            <div className="mt-3 flex gap-1" data-testid="admin-lang-switcher">
              {ADMIN_LANGS.map((lng) => {
                const active = (i18n.language || 'sk').split('-')[0] === lng;
                return (
                  <button
                    key={lng}
                    onClick={() => switchAdminLang(lng)}
                    className={`text-xs px-2 py-1 rounded transition ${
                      active
                        ? 'bg-orange-500 text-white font-semibold'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid={`admin-lang-${lng}`}
                  >
                    {lng.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          <nav className="mt-6 lg:mt-0 flex-1 overflow-y-auto pb-2">
            {navItems.map(item => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-6 py-3 transition ${
                    isActive(item.path, item.exact)
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                  data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
                >
                  <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                  <span className="truncate">{t(item.labelKey, item.fallback)}</span>
                </Link>
              );
            })}
          </nav>

          <div className="flex-shrink-0 p-4 border-t border-gray-800 bg-gray-900">
            <div className="flex items-center mb-4 min-w-0">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="ml-3 min-w-0">
                <div className="font-medium truncate">{user?.name}</div>
                <div className="text-sm text-gray-400 truncate">{user?.email}</div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-gray-300 hover:bg-gray-800 rounded transition"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-5 w-5 mr-3 flex-shrink-0" />
              {t('admin.logout')}
            </button>
            <Link
              to="/"
              className="flex items-center w-full px-4 py-2 mt-2 text-gray-300 hover:bg-gray-800 rounded transition"
            >
              <span className="mr-2">←</span> {t('nav.home')}
            </Link>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
